import React, { ReactNode } from 'react';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact, { BugsnagPluginReactResult } from '@bugsnag/plugin-react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import type { Theme } from '@mui/material/styles/createTheme';
import { RouterLink } from './router-link';
import { neutral } from '../../theme/colors';
import { runtimeConfig } from '../../util/runtimeConfig';

// Without a key (e.g. local development) Bugsnag stays off and errors are not reported.
const bugsnagEnabled = Boolean(runtimeConfig.BUGSNAG_KEY);

if (bugsnagEnabled) {
  Bugsnag.start({
    apiKey: runtimeConfig.BUGSNAG_KEY,
    plugins: [new BugsnagPluginReact(React)],
  });
}

const BugsnagErrorBoundary = bugsnagEnabled
  ? (Bugsnag.getPlugin('react') as BugsnagPluginReactResult).createErrorBoundary(React)
  : null;

const ErrorView = () => {
  const isSmallDeviceScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md')
  );

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <Box
      component="main"
      sx={{
        alignItems: 'center',
        display: 'flex',
        flexGrow: 1,
        py: '80px',
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 6,
          }}
        >
          <ErrorOutlineIcon
            sx={{
              fontSize: 120,
              color: 'error.main',
            }}
          />
        </Box>
        <Typography align="center" variant={isSmallDeviceScreen ? 'h1' : 'h4'}>
          Something went wrong
        </Typography>
        <Typography align="center" color="text.secondary" sx={{ mt: 0.5 }}>
          An unexpected error occurred. Please try refreshing the page or contact support
          if the problem persists.
        </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            mt: 6,
          }}
        >
          <Button variant="contained" color="primary" onClick={handleRefresh}>
            Try Again
          </Button>
          <Button
            sx={{
              height: '36px',
              '&:hover': {
                backgroundColor: neutral[800],
              },
            }}
            variant="text"
            component={RouterLink}
            href={'/'}
          >
            Back to Dashboard
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

// Used when Bugsnag is off: same fallback screen, nothing reported.
class UnreportedErrorBoundary extends React.Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    return this.state.hasError ? <ErrorView /> : this.props.children;
  }
}

const ErrorBoundaryComponent = ({ children }: { children: ReactNode }) => {
  if (!BugsnagErrorBoundary) {
    return <UnreportedErrorBoundary>{children}</UnreportedErrorBoundary>;
  }
  return (
    <BugsnagErrorBoundary FallbackComponent={ErrorView}>{children}</BugsnagErrorBoundary>
  );
};

export default ErrorBoundaryComponent;
